import React, { useState } from 'react'
import { useApolloClient } from 'react-apollo'
import { isApolloError } from 'apollo-client'

// Utils
import {
  confirmPendingSetupIntent,
  getPaymentTypeByProratedAmounts,
  getAppliedDiscount,
  ERROR_MESSAGE,
  hasPaymentDetails,
} from 'modules/workspace/utils'

// Components
import Modal from 'components/Modal'
import Button from 'components/Button'
import { ModalInjectedProps } from 'containers/ModalManager'
import { useToast } from 'containers/ToastManager/ToastProvider'
import { useStripe } from 'containers/StripeProvider'
import Discount from 'modules/workspace/components/Discount'
import Pluralize, { pluralize } from 'components/Pluralize'

// Local Components
import {
  SummaryHeader,
  SummaryLine,
  SummaryTotalLine,
  SummaryError,
  SummaryTotalDescribed,
  SummaryPaymentMethod,
  SummaryDiscountLine,
  SummaryProrated,
  ModalScheduleDisclaimer,
  MemberInvite,
} from 'modules/workspace/components/'

// Styles
import {
  StyledSummaryWrapper,
  StyledMemberList,
  StyledModalWarning,
  ModalDialog,
  ModalBody,
  Column,
  DiscountWrapper,
} from './InviteMembersModal.styles'

// Types
import {
  BillingPlanFragment,
  GetWorkspaceMembershipsDocument,
  GetWorkspaceMembershipsQuery,
  GetWorkspaceMembershipsQueryVariables,
  PaymentDetailsFragment,
  BillingSeatsInfoFragment,
  useGetSeatsUpdateBillingSimulationQuery,
  useInviteWorkspaceMembersMutation,
  useApplyCouponMutation,
  CouponFragment,
} from 'gqlTypes'

import { Member } from '../../types'

// Helpers
const getEditors = (members: Member[]) =>
  members.filter(member => member.isEditor).length

const getViewers = (members: Member[]) =>
  members.filter(member => !member.isEditor).length

const clamp = (num: number, min: number, max: number) => {
  return num <= min ? min : num >= max ? max : num
}

const buildWorkspaceMember = (member: Member) => ({
  email: member.email,
  accessLevel: member.isEditor ? ('EDITOR' as const) : ('VIEWER' as const),
  admin: !!member.isAdmin,
})

type DiscountError =
  | {
      code: string
      reason: string
    }
  | undefined
export interface InviteMembersModalProps extends ModalInjectedProps {
  customerId: string
  isOnTrial: boolean
  nextBillingCycleDate: string
  plan: BillingPlanFragment
  seats: BillingSeatsInfoFragment
  workspaceId: string
  paymentDetails: PaymentDetailsFragment
  isScheduledToBeCancelled?: boolean
}

/**
 * InviteMembersModal
 *
 * Renders the Invite Members modal.
 * Can be found, for example, in the Workspace Settings > General > Members panel.
 *
 */
export const InviteMembersModal: React.FC<InviteMembersModalProps> = ({
  customerId,
  hideModal,
  isOnTrial,
  nextBillingCycleDate,
  plan,
  seats,
  workspaceId,
  paymentDetails,
  isScheduledToBeCancelled,
}) => {
  const { load: loadStripe } = useStripe()
  const { showToast } = useToast()
  const apolloClient = useApolloClient()
  const [members, setMembers] = useState<Member[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [discountError, setDiscountError] = useState<DiscountError>(undefined)
  const [isDiscountAlreadyApplied, setIsDiscountAlreadyApplied] = useState<
    boolean | undefined
  >(undefined)

  const { availableSeats, scheduledSeatsTotal, currentSeatsTotal } = seats
  const numberOfEditorsAfterAvailableSeatsFilled = clamp(
    getEditors(members) - availableSeats,
    0,
    getEditors(members)
  )
  const numberOfPrepaidEditors = clamp(getEditors(members), 0, availableSeats)
  const showEditorsInSummary = !!numberOfEditorsAfterAvailableSeatsFilled
  const showPrePaidEditorsInSummary = !!availableSeats
  const hasScheduledSeatsChange = !!scheduledSeatsTotal
  const hasNewerWorkspaceMembers = members.length > 0
  const canSkipSCA = isOnTrial && isScheduledToBeCancelled

  // Handlers
  const handleInvite = async (newMember: Member) => {
    /**
     * Validate if the email already exists in the state list first
     */
    if (members.find(member => member.email === newMember.email)) {
      throw new Error('Already a Workspace Member')
    }

    /**
     * Make sure the user doesn't exist in the workspace before we add him to the list
     * not having this validation can influence the total due amount
     */
    const { data } = await apolloClient
      .query<
        GetWorkspaceMembershipsQuery,
        GetWorkspaceMembershipsQueryVariables
      >({
        query: GetWorkspaceMembershipsDocument,
        variables: { workspaceId, filter: newMember.email, limit: 10 },
        fetchPolicy: 'network-only',
      })
      .catch(() => {
        throw new Error('Something went wrong')
      })

    /**
     * Temporary fix to check if memberships returned by
     * email search (LIKE filter, instead of exact search)
     * include the provided email
     *
     * https://github.com/sketch-hq/cloud-frontend/pull/3466
     */
    const workspaceMemberships =
      data?.workspace?.memberships?.list?.entries || []

    const isWorkspaceMember = workspaceMemberships.some(
      member => member.user?.email === newMember.email && member.acceptedAt
    )

    if (isWorkspaceMember) {
      throw new Error('Already a Workspace Member')
    }

    const isAlreadyInvited = workspaceMemberships.some(
      member =>
        (member.user?.email === newMember.email && !member.acceptedAt) ||
        member.invite?.email === newMember.email
    )

    if (isAlreadyInvited) {
      throw new Error('This Member has already been invited')
    }

    setMembers(prevMembers => [...prevMembers, newMember])
  }

  const handleRemove = (member: Member) => {
    setMembers(prevMembers =>
      prevMembers.filter(prevMember => prevMember.email !== member.email)
    )
  }

  const updateMemberRole = (member: Member) => {
    setMembers(prevMembers =>
      prevMembers.map(prevMember =>
        prevMember.email === member.email ? member : prevMember
      )
    )
  }

  const handleEditor = (clickedMember: Member) => {
    updateMemberRole({ ...clickedMember, isEditor: true })
  }
  const handleViewer = (clickedMember: Member) => {
    updateMemberRole({ ...clickedMember, isEditor: false })
  }
  const handleToggleAdmin = (clickedMember: Member) => {
    updateMemberRole({ ...clickedMember, isAdmin: !clickedMember.isAdmin })
  }

  const handleInviteMembers = async () => {
    setIsLoading(true)

    try {
      const { data } = await inviteMembers({
        variables: {
          input: {
            workspaceIdentifier: workspaceId,
            members: members.map(buildWorkspaceMember),
          },
        },
      })

      const { pendingScaToken } = data?.inviteWorkspaceMembers || {}

      /**
       * If "pendingScaToken" is present on the payload we need to do the
       * challenge with stripe to make sure the card is not INCOMPLETE
       */
      if (pendingScaToken && !canSkipSCA) {
        const stripe = await loadStripe()

        await confirmPendingSetupIntent(stripe, pendingScaToken)
      }

      showToast(
        `${pluralize('Member', 'Members', members.length)} added successfully`,
        'positive'
      )
      hideModal()
    } catch (error) {
      // Ignore this error, mutation errors will handle by the mutation `onError`
    }

    setIsLoading(false)
  }

  // QUERIES
  const {
    loading: isBillingSummaryLoading,
    data: billingSummaryData,
    error: billingSummaryError,
    refetch,
  } = useGetSeatsUpdateBillingSimulationQuery({
    fetchPolicy: 'network-only',
    variables: {
      customerId,
      totalSeats: currentSeatsTotal + numberOfEditorsAfterAvailableSeatsFilled,
    },
    /* Prevent unneeded queries from being done */
    skip: numberOfEditorsAfterAvailableSeatsFilled === 0,
    onCompleted: data => {
      if (numberOfEditorsAfterAvailableSeatsFilled === 0) {
        return
      }

      if (isDiscountAlreadyApplied === undefined) {
        setIsDiscountAlreadyApplied(
          !!data?.seatsUpdateBillingSimulation.couponInfo
        )
      }
    },
  })

  // MUTATIONS
  const [
    inviteMembers,
    { loading: invitationLoading },
  ] = useInviteWorkspaceMembersMutation({
    onError: 'show-toast',
    refetchQueries: () => [
      {
        query: GetWorkspaceMembershipsDocument,
        variables: { workspaceId } as GetWorkspaceMembershipsQueryVariables,
      },
    ],
  })

  const { prorated, extraSeatsAmount } =
    billingSummaryData?.seatsUpdateBillingSimulation || {}

  const paymentType = getPaymentTypeByProratedAmounts(
    prorated?.chargeAmount,
    prorated?.creditsAmount
  )

  const [
    applyCoupon,
    { loading: isLoadingApplyCoupon },
  ] = useApplyCouponMutation({ onError: 'unsafe-throw-exception' })

  const appliedDiscount = getAppliedDiscount(
    billingSummaryData?.seatsUpdateBillingSimulation?.couponInfo || undefined
  )

  const isDiscountFieldVisible =
    numberOfEditorsAfterAvailableSeatsFilled !== 0 &&
    hasPaymentDetails(paymentDetails)

  const handleApplyDiscount = async (discountCode: string) => {
    try {
      await applyCoupon({
        variables: {
          customerId,
          promotionCode: discountCode,
        },
      })

      refetch()
      setDiscountError(undefined)
    } catch (error) {
      if (isApolloError(error)) {
        const code = error?.graphQLErrors[0]?.extensions
          ?.code as CouponFragment['errorCode']

        const isValidCode = !!code && Object.keys(ERROR_MESSAGE).includes(code)

        setDiscountError({
          code: discountCode,
          reason: isValidCode ? ERROR_MESSAGE[code!] : ' is invalid',
        })
      } else {
        setDiscountError({
          code: '',
          reason: error.message,
        })
      }
    }
  }

  return (
    <ModalDialog onCancel={hideModal}>
      <Modal.Header>Invite Members to Your Workspace</Modal.Header>
      <ModalBody>
        <Column>
          <MemberInvite onInvite={handleInvite} isDisabled={false} />
          <StyledMemberList
            members={members}
            onRemove={handleRemove}
            onEditor={handleEditor}
            onViewer={handleViewer}
            onToggleAdmin={handleToggleAdmin}
            isDisabled={false}
            hasSingleEditor={false}
          />
        </Column>
        <Column>
          <StyledSummaryWrapper>
            {billingSummaryError ? (
              <SummaryError refetch={() => refetch()} />
            ) : (
              <>
                <SummaryHeader
                  plan={plan}
                  paymentMethod={
                    // Wait for the BE response to show the proper payment method
                    prorated && (
                      <SummaryPaymentMethod
                        chargeMethod={paymentDetails}
                        paymentType={paymentType}
                      />
                    )
                  }
                />
                {showEditorsInSummary && (
                  <SummaryLine
                    description={`${numberOfEditorsAfterAvailableSeatsFilled} ${pluralize(
                      'Editor',
                      'Editors',
                      numberOfEditorsAfterAvailableSeatsFilled
                    )}`}
                    value={extraSeatsAmount || 0}
                    loading={isBillingSummaryLoading}
                  />
                )}
                {showPrePaidEditorsInSummary && (
                  <SummaryLine
                    description={`${numberOfPrepaidEditors} ${pluralize(
                      'Editor',
                      'Editors',
                      numberOfPrepaidEditors
                    )} (pre-paid Seats)`}
                    value={0}
                  />
                )}
                <SummaryLine
                  description={`${getViewers(members)} ${pluralize(
                    'Viewer',
                    'Viewers',
                    getViewers(members)
                  )}`}
                  value={'Free'}
                />
                {isDiscountFieldVisible && (
                  <DiscountWrapper>
                    <Discount
                      onApplyDiscountCode={handleApplyDiscount}
                      error={discountError}
                      isDisabled={isLoadingApplyCoupon}
                      isLoading={isLoadingApplyCoupon}
                    />
                  </DiscountWrapper>
                )}
                {appliedDiscount && (
                  <SummaryDiscountLine
                    appliedDiscount={appliedDiscount}
                    isAlreadyApplied={isDiscountAlreadyApplied}
                  />
                )}
                {prorated && (
                  <SummaryProrated
                    daysElapsedInCycle={prorated.daysElapsedInCycle}
                    refundAmountForNewSeats={
                      prorated.proratedRefundAmountForNewSeats
                    }
                  />
                )}
                <SummaryTotalLine
                  description={
                    isOnTrial ? 'Total Due During Trial' : 'Total due'
                  }
                  value={prorated?.amount || 0}
                  loading={isBillingSummaryLoading}
                />
                <SummaryTotalDescribed
                  charge={prorated?.chargeAmount}
                  credit={prorated?.creditsAmount}
                />
              </>
            )}
          </StyledSummaryWrapper>
          <StyledModalWarning
            title="What happens after the trial period?"
            description="If you continue with a subscription after your trial ends, you’ll be charged for Editors and any Unused Seats you have.
You won’t be charged during your trial period."
            show={isOnTrial}
          />
          {hasScheduledSeatsChange && (
            <ModalScheduleDisclaimer
              hideModal={hideModal}
              nextBillingCycleDate={nextBillingCycleDate}
              workspaceId={workspaceId}
              scheduledSeats={scheduledSeatsTotal!}
            />
          )}
        </Column>
      </ModalBody>
      <Modal.Footer>
        <Button onClick={hideModal} variant="secondary">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleInviteMembers}
          loading={invitationLoading || !customerId || isLoading}
          disabled={!hasNewerWorkspaceMembers}>
          <Pluralize
            singular="Add Member"
            plural="Add Members"
            count={members.length}
          />
        </Button>
      </Modal.Footer>
    </ModalDialog>
  )
}
